import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, ArrowRight, Shield, Zap, 
  HelpCircle, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const Pricing = () => {
  return (
    <div className="min-h-screen bg-slate-950 pt-20" data-testid="pricing-page">
      {/* Hero */}
      <section className="section-spacing relative">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        
        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-4 py-2 mb-6">
              <Zap className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">Simple, Transparent Pricing</span>
            </div>

            <h1 className="text-hero text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Protection That <span className="text-cyan-400">Fits Your Budget</span>
            </h1>

            <p className="text-body max-w-2xl mx-auto">
              No hidden fees. No complicated terms. Just straightforward protection for Indian creators.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20 md:pb-32" data-testid="pricing-cards">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Monthly Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-base relative"
            >
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Monthly Plan</h3>
                <p className="text-slate-400">Perfect for getting started</p>
              </div>

              <div className="text-center mb-8">
                <span className="text-slate-400 text-lg">₹</span>
                <span className="text-6xl font-bold text-cyan-400" style={{ fontFamily: 'Outfit, sans-serif' }}>2,000</span>
                <span className="text-slate-400">/month</span>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>24/7 Social Media Monitoring</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Real-time Threat Alerts</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Impersonation Detection</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Basic Insurance Coverage (₹1L)</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Email Support</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Monthly Safety Report</span>
                </li>
              </ul>

              <Link to="/contact" className="block">
                <Button 
                  className="w-full rounded-full py-6 font-bold text-base transition-all bg-slate-800/50 text-white border border-slate-700 hover:border-cyan-400/50 hover:bg-slate-800"
                  data-testid="pricing-cta-0"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>

            {/* Quarterly Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card-base relative border-cyan-400/50 glow"
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-cyan-400 text-slate-950 px-4 py-1 rounded-full text-sm font-bold">
                MOST POPULAR
              </div>

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Quarterly Plan</h3>
                <p className="text-slate-400">Best value - Save ₹1,000</p>
              </div>

              <div className="text-center mb-8">
                <span className="text-slate-400 text-lg">₹</span>
                <span className="text-6xl font-bold text-cyan-400" style={{ fontFamily: 'Outfit, sans-serif' }}>5,000</span>
                <span className="text-slate-400">/quarter</span>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Priority WhatsApp Support</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Extended Insurance Coverage (₹5L)</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Legal Consultation (1hr/quarter)</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Dedicated Account Manager</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Weekly Safety Reports</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Crisis Response Team Access</span>
                </li>
              </ul>

              <Link to="/contact" className="block">
                <Button 
                  className="w-full rounded-full py-6 font-bold text-base transition-all bg-cyan-400 text-slate-950 hover:bg-cyan-300 hover:scale-[1.02]"
                  data-testid="pricing-cta-1"
                >
                  Get Best Value
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>

          {/* Enterprise CTA */}
          <motion.div 
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-4 bg-slate-900/50 border border-slate-800 rounded-2xl px-6 py-4">
              <Shield className="w-6 h-6 text-cyan-400" />
              <span className="text-slate-300">
                Have 1M+ followers? <Link to="/contact" className="text-cyan-400 hover:text-cyan-300 font-medium">Contact us for enterprise pricing</Link>
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-spacing bg-slate-900/30" data-testid="whats-included">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What's <span className="text-cyan-400">Included</span> in Every Plan
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-start gap-4 p-6 bg-slate-900/50 border border-slate-800 rounded-xl"
            >
              <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-white mb-1">Multi-Platform Monitoring</h4>
                <p className="text-sm text-slate-400">Instagram, YouTube, X, Facebook & LinkedIn</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 }}
              className="flex items-start gap-4 p-6 bg-slate-900/50 border border-slate-800 rounded-xl"
            >
              <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-white mb-1">AI-Powered Detection</h4>
                <p className="text-sm text-slate-400">Smart threat identification</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="flex items-start gap-4 p-6 bg-slate-900/50 border border-slate-800 rounded-xl"
            >
              <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-white mb-1">Real-Time Alerts</h4>
                <p className="text-sm text-slate-400">Instant notifications on your phone</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="flex items-start gap-4 p-6 bg-slate-900/50 border border-slate-800 rounded-xl"
            >
              <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-white mb-1">Insurance Coverage</h4>
                <p className="text-sm text-slate-400">Financial protection for cyber incidents</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex items-start gap-4 p-6 bg-slate-900/50 border border-slate-800 rounded-xl"
            >
              <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-white mb-1">Takedown Assistance</h4>
                <p className="text-sm text-slate-400">Help removing fake accounts</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.25 }}
              className="flex items-start gap-4 p-6 bg-slate-900/50 border border-slate-800 rounded-xl"
            >
              <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-white mb-1">Safety Reports</h4>
                <p className="text-sm text-slate-400">Regular updates on your protection status</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-spacing bg-slate-950" data-testid="faqs-section">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 mb-4">
              <HelpCircle className="w-5 h-5 text-cyan-400" />
              <span className="text-cyan-400 font-medium">FAQs</span>
            </div>
            <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Common <span className="text-cyan-400">Questions</span>
            </h2>
          </motion.div>

          <motion.div 
            className="max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-0" className="bg-slate-900/50 border border-slate-800 rounded-xl px-6 data-[state=open]:border-cyan-400/30">
                <AccordionTrigger className="text-left text-white font-semibold hover:text-cyan-400 hover:no-underline py-6" data-testid="faq-trigger-0">
                  What platforms do you monitor?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 pb-6 leading-relaxed">
                  We currently monitor Instagram, YouTube, X (Twitter), Facebook, and LinkedIn. We track mentions, comments, tags, impersonation attempts, and potential security threats across all these platforms.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-1" className="bg-slate-900/50 border border-slate-800 rounded-xl px-6 data-[state=open]:border-cyan-400/30">
                <AccordionTrigger className="text-left text-white font-semibold hover:text-cyan-400 hover:no-underline py-6" data-testid="faq-trigger-1">
                  How does the insurance coverage work?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 pb-6 leading-relaxed">
                  Our insurance is a group professional liability policy specifically designed for digital creators. It covers legal defense costs, settlement amounts, income loss due to cyber incidents, and crisis management expenses. Coverage limits depend on your plan.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-slate-900/50 border border-slate-800 rounded-xl px-6 data-[state=open]:border-cyan-400/30">
                <AccordionTrigger className="text-left text-white font-semibold hover:text-cyan-400 hover:no-underline py-6" data-testid="faq-trigger-2">
                  What happens when a threat is detected?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 pb-6 leading-relaxed">
                  You receive an instant alert via your preferred channel (WhatsApp, email, or app). Our team provides guidance on the best response. For serious threats, we help with platform reporting, evidence documentation, and if needed, legal escalation.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-slate-900/50 border border-slate-800 rounded-xl px-6 data-[state=open]:border-cyan-400/30">
                <AccordionTrigger className="text-left text-white font-semibold hover:text-cyan-400 hover:no-underline py-6" data-testid="faq-trigger-3">
                  Can I cancel anytime?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 pb-6 leading-relaxed">
                  Yes, you can cancel your subscription anytime. Monthly plans have no lock-in period. Quarterly plans are non-refundable once started but you can choose not to renew.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-slate-900/50 border border-slate-800 rounded-xl px-6 data-[state=open]:border-cyan-400/30">
                <AccordionTrigger className="text-left text-white font-semibold hover:text-cyan-400 hover:no-underline py-6" data-testid="faq-trigger-4">
                  Is my data secure?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 pb-6 leading-relaxed">
                  Absolutely. We use bank-level encryption and read-only access to your social accounts. We never store your passwords and cannot post on your behalf. Your data is never sold to third parties.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-slate-900/50 border border-slate-800 rounded-xl px-6 data-[state=open]:border-cyan-400/30">
                <AccordionTrigger className="text-left text-white font-semibold hover:text-cyan-400 hover:no-underline py-6" data-testid="faq-trigger-5">
                  What if I have more than 1 million followers?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 pb-6 leading-relaxed">
                  For creators with 1M+ followers, we offer custom enterprise plans with higher coverage limits, dedicated support teams, and tailored monitoring. Contact us for a personalized quote.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-slate-900/50 relative" data-testid="pricing-cta-section">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Still Have <span className="text-cyan-400">Questions</span>?
            </h2>
            <p className="text-body max-w-2xl mx-auto mb-8">
              Our team is here to help. Chat with us on WhatsApp for instant answers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/919986573456"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 text-white px-8 py-4 rounded-full font-bold transition-all hover:scale-105"
                data-testid="whatsapp-cta"
              >
                <MessageCircle className="w-5 h-5" />
                Chat on WhatsApp
              </a>
              <Link to="/contact">
                <Button 
                  variant="outline"
                  className="bg-slate-800/50 text-white border border-slate-700 hover:border-cyan-400/50 hover:bg-slate-800 rounded-full px-8 py-4 font-medium transition-all"
                  data-testid="contact-us-cta"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
